import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

const SingleTabEnforcer = ({ children }) => {
  const [isActiveTab, setIsActiveTab] = useState(true);
  const navigate = useNavigate();
  const tabIdRef = useRef(Math.random().toString(36).substring(2)); // Persistent tabId
  const isInitialCheckDone = useRef(false);

  useEffect(() => {
    const SESSION_KEY = "activeTab";
    const HEARTBEAT_INTERVAL = 5000; // 5 seconds
    const TIMEOUT = 15000; // 15 seconds
    const tabId = tabIdRef.current;

    // Check and set active tab
    const checkActiveTab = () => {
      const storedTab = localStorage.getItem(SESSION_KEY);
      let isActive = true;

      if (!storedTab) {
        // No active tab, claim it
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ tabId, timestamp: Date.now() }),
        );
        console.log(`Tab ${tabId} claimed active status (no stored tab)`);
      } else {
        try {
          const { tabId: storedTabId, timestamp } = JSON.parse(storedTab);
          if (storedTabId === tabId) {
            // This tab is active
            console.log(`Tab ${tabId} is active`);
          } else if (Date.now() - timestamp > TIMEOUT) {
            // Stored tab is stale, claim active status
            localStorage.setItem(
              SESSION_KEY,
              JSON.stringify({ tabId, timestamp: Date.now() }),
            );
            console.log(`Tab ${tabId} claimed active status (stale tab)`);
          } else {
            // Another tab is active
            isActive = false;
            console.log(
              `Tab ${tabId} is inactive, another tab (${storedTabId}) is active`,
            );
            navigate("app/already-open", { replace: true });
          }
        } catch (e) {
          // Invalid JSON, claim active status
          localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ tabId, timestamp: Date.now() }),
          );
          console.log(`Tab ${tabId} claimed active status (invalid JSON)`);
        }
      }

      setIsActiveTab(isActive);
      isInitialCheckDone.current = true;
    };

    // Delay initial check to avoid race conditions
    const initialCheckTimeout = setTimeout(checkActiveTab, 2000);

    // Handle storage events
    const handleStorageChange = (event) => {
      if (event.key === SESSION_KEY && event.newValue) {
        try {
          const { tabId: storedTabId } = JSON.parse(event.newValue);
          if (storedTabId !== tabId && isInitialCheckDone.current) {
            console.log(
              `Tab ${tabId} detected new active tab (${storedTabId}), navigating`,
            );
            setIsActiveTab(false);
            navigate("app/already-open", { replace: true });
          } else {
            console.log(
              `Tab ${tabId} ignored storage event (self or pre-initial)`,
            );
          }
        } catch (e) {
          console.log(`Tab ${tabId} ignored invalid storage event`);
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    // Periodic heartbeat
    const interval = setInterval(() => {
      if (!isActiveTab) return; // Skip heartbeat if already inactive
      const storedTab = localStorage.getItem(SESSION_KEY);
      if (storedTab) {
        try {
          const { tabId: storedTabId, timestamp } = JSON.parse(storedTab);
          if (storedTabId === tabId) {
            // Update timestamp
            localStorage.setItem(
              SESSION_KEY,
              JSON.stringify({ tabId, timestamp: Date.now() }),
            );
            console.log(`Tab ${tabId} updated timestamp`);
          } else if (Date.now() - timestamp > TIMEOUT) {
            // Claim active status
            localStorage.setItem(
              SESSION_KEY,
              JSON.stringify({ tabId, timestamp: Date.now() }),
            );
            setIsActiveTab(true);
            console.log(`Tab ${tabId} claimed active status (stale tab)`);
          } else {
            // Another tab is active
            setIsActiveTab(false);
            console.log(
              `Tab ${tabId} detected active tab (${storedTabId}), navigating`,
            );
            navigate("app/already-open", { replace: true });
          }
        } catch (e) {
          // Claim active status
          localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ tabId, timestamp: Date.now() }),
          );
          setIsActiveTab(true);
          console.log(`Tab ${tabId} claimed active status (invalid JSON)`);
        }
      }
    }, HEARTBEAT_INTERVAL);

    // Cleanup on tab close
    const handleBeforeUnload = () => {
      try {
        const storedTab = localStorage.getItem(SESSION_KEY);
        if (storedTab) {
          const { tabId: storedTabId } = JSON.parse(storedTab);
          if (storedTabId === tabId) {
            localStorage.removeItem(SESSION_KEY);
            console.log(`Tab ${tabId} cleared active status on close`);
          }
        }
      } catch (e) {
        console.log(`Tab ${tabId} ignored error during cleanup`);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      clearTimeout(initialCheckTimeout);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearInterval(interval);
      console.log(`Tab ${tabId} cleaned up event listeners`);
    };
  }, [navigate, isActiveTab]);

  // Always render children to allow app/already-open route to render
  return children;
};

export default SingleTabEnforcer;
